import { AudioWaveformPlayer } from "@/components/shared/audio-waveform-player";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import {
  getCategoryLabel,
  type DeepThoughtsDefinition,
  type DeepThoughtsResult,
} from "@somethings-ui/core/deep-thoughts";
import { DateTime } from "luxon";

interface DeepThoughtsSummaryContentProps {
  result: DeepThoughtsResult;
  definition: DeepThoughtsDefinition | undefined;
  completedAt: string | null | undefined;
  showHeader?: boolean;
}

function Tag({ label }: { label: string }) {
  return (
    <span className="inline-flex rounded-full bg-white px-3 py-1 font-twk text-sm text-text-tertiary">
      {label}
    </span>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl bg-surfaces-z3 p-4">
      <h2 className="font-avantt text-base font-bold leading-[1.2] tracking-[-0.16px] text-text-primary">
        {title}
      </h2>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function VoiceNote({ audioS3Key }: { audioS3Key: string }) {
  const { url } = useSignedMedia(audioS3Key);
  if (!url) return null;
  return <AudioWaveformPlayer audioUrl={url} variant="light" />;
}

export function DeepThoughtsSummaryContent({
  result,
  definition,
  completedAt,
  showHeader = true,
}: DeepThoughtsSummaryContentProps) {
  const { q1, q2, q3 } = result.responses;
  const categories = definition?.staticContent.categories.options ?? [];
  const q1Labels = q1.map((value) => getCategoryLabel(value, categories));
  const heaviestLabel = getCategoryLabel(q2.heaviest, categories);

  const completedAtLabel = completedAt
    ? DateTime.fromISO(completedAt).toFormat("MMM d, h:mm a")
    : null;

  return (
    <div className="flex flex-col gap-6">
      {showHeader ? (
        <div className="flex flex-col gap-2 px-4 pt-6">
          <h1 className="font-avantt text-2xl font-bold leading-[1.15] tracking-[-0.24px] text-text-primary">
            Deep Thoughts summary
          </h1>
          {completedAtLabel ? (
            <p className="font-twk text-sm text-tertiary">
              {completedAtLabel}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-col gap-6 px-4 pb-6">
        <Section title="What's been on your mind lately?">
          <div className="flex flex-wrap gap-2">
            {q1Labels.map((label) => (
              <Tag key={label} label={label} />
            ))}
          </div>
        </Section>

        <Section title="Which one feels the heaviest and why?">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Tag label={heaviestLabel} />
            </div>
            {q2.extraText ? (
              <p className="font-twk text-sm leading-[1.4] text-secondary">
                {q2.extraText}
              </p>
            ) : null}
          </div>
        </Section>

        <Section title="Is there something going on right now that you haven't figured out how to talk about yet?">
          {q3.type === "none" ? (
            <p className="font-twk text-sm text-secondary">
              Teen chose not to share more.
            </p>
          ) : q3.type === "text" ? (
            <p className="font-twk text-sm leading-[1.4] text-secondary">
              {q3.text}
            </p>
          ) : (
            <div className="flex flex-col gap-4">
              <VoiceNote audioS3Key={q3.audioS3Key} />
              {q3.transcript ? (
                <p className="font-twk text-sm leading-[1.4] text-secondary">
                  {q3.transcript}
                </p>
              ) : (
                <p className="font-twk text-sm italic text-secondary">
                  Transcript unavailable
                </p>
              )}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}
